import { CARS } from '../data/cars.ts';
import { realKmh } from '../data/scale.ts';
import { ROUTES } from '../data/routes.ts';
import { CATEGORIES, categoryOf, type Category, type CategoryId } from '../../shared/categories.ts';
import type { ApiMode, GarageResponse } from '../../shared/api.ts';
import type { CarParams } from '../sim/types.ts';
import metrics from '../data/carMetrics.json' with { type: 'json' };

/**
 * The garage's order and words: which cars show, in what order, under which
 * headings, and what each says about itself. No DOM here; main.ts builds the
 * screen from it.
 *
 * The numbers shown are the real car's, or measured or counted, never rated:
 *
 *   0-100    and top speed, the real car's (data/cars.ts);
 *   drift    TAKA's Drift Run score in the car, the best car 100;
 *   top 10s  places the car holds in each course's overall top 10, from the
 *            boards (GET /api/garage).
 *
 * Pace (% off the Tom's, tools/car-metrics.mjs) is not shown, but it is what
 * orders each category: fastest first.
 */

export type GarageSort = 'category' | 'accel' | 'speed' | 'drift' | 'boards';
export type GarageFilter = 'all' | CategoryId;

export interface CarMetrics {
  simVersion: number;
  pace: Record<string, number>;
  drift: Record<string, number>;
}

export const METRICS = metrics as CarMetrics;

/** What each category is, in a line under its heading. */
export const CATEGORY_LINES: Record<CategoryId, string> = {
  gt: 'Race cars and the fastest road cars: the quickest against the clock.',
  sport: 'Quick road cars and drift builds, within a tenth of the Tom’s.',
  street: 'Light tuned street cars: the slowest, and the purest to throw sideways.',
};

export interface CarGroup {
  /** The heading, in the category order; null for one list in a measured order. */
  category: Category | null;
  cars: CarParams[];
}

const pace = (car: CarParams) => METRICS.pace[car.id] ?? Infinity;
const drift = (car: CarParams) => METRICS.drift[car.id] ?? 0;
const tops = (car: CarParams, boards: GarageResponse['boards'] | null) => {
  const b = boards?.[car.id];
  return b ? b.drift + b.time : 0;
};

/**
 * The cars to show, in order. By category: a group per category, fastest
 * first, so a locked car -- the best in its category -- heads its group. By a
 * measure: one list, best first, ties broken by pace.
 */
export function orderCars(sort: GarageSort, filter: GarageFilter, boards: GarageResponse['boards'] | null): CarGroup[] {
  const shown = CARS.filter((c) => filter === 'all' || categoryOf(c.id)?.id === filter);
  if (sort === 'category') {
    return CATEGORIES.filter((cat) => filter === 'all' || cat.id === filter)
      .map((cat) => ({ category: cat, cars: shown.filter((c) => categoryOf(c.id)?.id === cat.id).sort((a, b) => pace(a) - pace(b)) }))
      .filter((g) => g.cars.length > 0);
  }
  const by: Record<Exclude<GarageSort, 'category'>, (a: CarParams, b: CarParams) => number> = {
    accel: (a, b) => (a.zeroTo100 ?? Infinity) - (b.zeroTo100 ?? Infinity) || pace(a) - pace(b),
    speed: (a, b) => b.handling.topSpeed - a.handling.topSpeed || pace(a) - pace(b),
    drift: (a, b) => drift(b) - drift(a) || pace(a) - pace(b),
    boards: (a, b) => tops(b, boards) - tops(a, boards) || pace(a) - pace(b),
  };
  return [{ category: null, cars: [...shown].sort(by[sort]) }];
}

/** "3.7s": the real car's 0-100km/h. */
export function formatZeroTo100(car: CarParams): string {
  return car.zeroTo100 ? `${car.zeroTo100.toFixed(1)}s` : '—';
}

/** "290": the real car's top speed, km/h. */
export function formatTopSpeed(car: CarParams): string {
  return String(Math.round(realKmh(car.handling.topSpeed)));
}

export function formatDrift(carId: string): string {
  const index = METRICS.drift[carId];
  return index === undefined ? '—' : String(index);
}

/** "4 · 2": top-10 places in Drift Run and in Time Attack; a dash until the boards answer. */
export function formatTops(carId: string, boards: GarageResponse['boards'] | null): string {
  if (!boards) return '—';
  const b = boards[carId];
  return b ? `${b.drift} · ${b.time}` : '0 · 0';
}

const MODE_NAMES: Partial<Record<ApiMode, string>> = {
  driftRun: 'Drift Run',
  timeAttack: 'Time Attack',
  timeAttackPro: 'Time Attack Pro',
};

/** "#2 in SPORT · Estoril, Drift Run": the player's best place in a car, on its category's board. */
export function placeLine(carId: string, mine: GarageResponse['mine'] | null): string | null {
  const best = mine?.[carId];
  if (!best) return null;
  const course = ROUTES.find((r) => r.id === best.routeId)?.name ?? best.routeId;
  const category = categoryOf(carId)?.name ?? '';
  const courseName = course.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
  return `#${best.place} in ${category} · ${courseName}, ${MODE_NAMES[best.mode] ?? best.mode}`;
}
